/**
 * @ims/shared — Config Schema Validation
 *
 * Validates JSON config objects against Ajv JSON Schema.
 * Schemas live in packages/shared/schemas/*.schema.json
 *
 * @see docs/ims-brand-frontends/02-config-system.md — Schema-Validierungs-Strategie
 */

import Ajv from 'ajv';

import brandSchema from '../../schemas/brand.schema.json';
import modelSchema from '../../schemas/model.schema.json';
import colorsSchema from '../../schemas/colors.schema.json';
import marketSchema from '../../schemas/market.schema.json';
import intersectionSchema from '../../schemas/intersection.schema.json';

// ---------------------------------------------------------------------------
// Ajv instance (shared — compiled validators are cached)
// ---------------------------------------------------------------------------

const ajv = new Ajv({ allErrors: true });

// Pre-compile all schemas once at module load time
const validators = {
  brand: ajv.compile(brandSchema),
  model: ajv.compile(modelSchema),
  colors: ajv.compile(colorsSchema),
  market: ajv.compile(marketSchema),
  intersection: ajv.compile(intersectionSchema),
} as const;

// ---------------------------------------------------------------------------
// Public schema keys
// ---------------------------------------------------------------------------

export type ConfigSchemaKey = keyof typeof validators;

// ---------------------------------------------------------------------------
// Validation Result
// ---------------------------------------------------------------------------

export interface ValidationSuccess<T> {
  valid: true;
  data: T;
  errors: null;
}

export interface ValidationFailure {
  valid: false;
  data: null;
  errors: string[];
}

export type ValidationResult<T> = ValidationSuccess<T> | ValidationFailure;

// ---------------------------------------------------------------------------
// validateConfig
// ---------------------------------------------------------------------------

/**
 * Validate a plain object against a named config schema.
 *
 * @example
 * const result = validateConfig('brand', rawJson);
 * if (result.valid) {
 *   // result.data is typed as BrandConfig
 * } else {
 *   console.error(result.errors);
 * }
 */
export function validateConfig<T>(
  schemaKey: ConfigSchemaKey,
  data: unknown,
): ValidationResult<T> {
  const validate = validators[schemaKey];
  const valid = validate(data) as boolean;

  if (valid) {
    return { valid: true, data: data as T, errors: null };
  }

  const errors = (validate.errors ?? []).map((err) => {
    const path = err.instancePath || '(root)';
    return `${path}: ${err.message ?? 'unknown error'}`;
  });

  return { valid: false, data: null, errors };
}
